import { Helmet } from "react-helmet-async";
import { absoluteUrl } from "@/lib/social";

interface SocialMetaProps {
  title: string;
  description?: string | null;
  path: string;
  image?: string | null;
  type?: "article" | "website" | "event";
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

/**
 * Per-route Open Graph + Twitter Card + JSON-LD wrapper.
 * Note: this mutates head client-side, so JS-aware crawlers (Google, X)
 * pick it up; non-JS crawlers (LinkedIn, Slack, Facebook) still use the
 * sitewide tags in index.html as fallback.
 */
export default function SocialMeta({
  title,
  description,
  path,
  image,
  type = "article",
  jsonLd,
}: SocialMetaProps) {
  const url = absoluteUrl(path);
  const safeDesc = (description ?? "").slice(0, 300);
  const jsonLdArr = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Helmet>
      <title>{title}</title>
      {safeDesc && <meta name="description" content={safeDesc} />}
      <link rel="canonical" href={url} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      {safeDesc && <meta property="og:description" content={safeDesc} />}
      <meta property="og:url" content={url} />
      {image && <meta property="og:image" content={image} />}
      <meta name="twitter:card" content={image ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={title} />
      {safeDesc && <meta name="twitter:description" content={safeDesc} />}
      {image && <meta name="twitter:image" content={image} />}
      {jsonLdArr.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}